import type { WorkspaceHandoff } from "@rakazo/contracts";
import {
  CONVERSATION_REFERENCE_MAX_COUNT,
  WorkspaceHandoffSchema,
  workspaceHandoffDestination,
} from "@rakazo/contracts";

/** Identity only. Disk recovery never restores authorization. */
export const ConversationIdentitySchema = WorkspaceHandoffSchema.refine(
  (value) =>
    value.app === "messages" && Boolean(value.id) && !value.group && !value.terminal && !value.view,
);
export type ConversationIdentity = WorkspaceHandoff;
export type PendingConversation = { id: string; threadKey: string; source: ConversationIdentity };
export const PendingConversationDraftSchema = {
  parse(value: unknown): PendingConversation[] {
    if (!Array.isArray(value) || value.length > CONVERSATION_REFERENCE_MAX_COUNT)
      throw new Error("Invalid conversation draft");
    return value.map((item) => {
      if (
        !item ||
        typeof item.id !== "string" ||
        !item.id ||
        item.id.length > 200 ||
        typeof item.threadKey !== "string" ||
        !item.threadKey ||
        item.threadKey.length > 200 ||
        Object.keys(item).some((key) => !["id", "threadKey", "source"].includes(key))
      )
        throw new Error("Invalid conversation draft");
      return {
        id: item.id,
        threadKey: item.threadKey,
        source: ConversationIdentitySchema.parse(item.source),
      };
    });
  },
  safeParse(value: unknown): { success: true; data: PendingConversation[] } | { success: false } {
    try {
      return { success: true, data: this.parse(value) };
    } catch {
      return { success: false };
    }
  },
};
export type ConversationRecipient = { id: string; spaceId: string };
export const CONVERSATION_UNAVAILABLE = "Conversation unavailable";

/** Proposed additive API binding; the server must authorize every operation, not trust a prior grant. */
export type ConversationAccess = (input: {
  source: ConversationIdentity;
  recipient: ConversationRecipient;
  operation: "stage" | "send" | "open";
}) => Promise<void>;

export function sameConversation(a: ConversationIdentity, b: ConversationIdentity) {
  return (
    a.spaceId === b.spaceId &&
    a.id === b.id &&
    a.messageId === b.messageId &&
    a.peerBotId === b.peerBotId &&
    a.routineId === b.routineId
  );
}
export function appendConversation(
  latest: readonly PendingConversation[],
  next: PendingConversation,
): PendingConversation[] {
  const parsed = PendingConversationDraftSchema.parse([next])[0]!;
  if (
    latest.some(
      (item) => item.threadKey === parsed.threadKey && sameConversation(item.source, parsed.source),
    )
  )
    return latest as PendingConversation[];
  return PendingConversationDraftSchema.parse([...latest, parsed]);
}

/** One picker operation, not a grant. Cancellation and a new selection invalidate late replies. */
export function createConversationHandoff() {
  let generation = 0;
  return {
    cancel() {
      generation++;
    },
    async stage({
      source,
      recipient,
      authorize,
      commit,
      navigate,
    }: {
      source: ConversationIdentity;
      recipient: ConversationRecipient;
      authorize: ConversationAccess;
      // Read the current durable store here, never a render/picker-open snapshot.
      commit: (source: ConversationIdentity, recipient: ConversationRecipient) => void;
      navigate: (recipient: ConversationRecipient) => void;
    }): Promise<boolean> {
      const current = ++generation;
      const identity = ConversationIdentitySchema.parse(source);
      const target = { ...recipient };
      try {
        await authorize({ source: identity, recipient: target, operation: "stage" });
      } catch {
        if (current !== generation) return false;
        throw new Error(CONVERSATION_UNAVAILABLE);
      }
      if (current !== generation) return false;
      // No asynchronous gap between checking current selection and merging the latest store.
      commit(identity, target);
      navigate(target);
      return true;
    },
    async open(
      source: ConversationIdentity,
      recipient: ConversationRecipient,
      authorize: ConversationAccess,
    ) {
      const current = ++generation;
      const identity = ConversationIdentitySchema.parse(source);
      try {
        await authorize({ source: identity, recipient, operation: "open" });
      } catch {
        if (current !== generation) return null;
        throw new Error(CONVERSATION_UNAVAILABLE);
      }
      if (current !== generation) return null;
      return workspaceHandoffDestination(identity);
    },
  };
}

/** Preflight only; explicit send must also carry these identities for atomic server revalidation. */
export async function conversationSendReferences(
  items: PendingConversation[],
  recipient: ConversationRecipient,
  authorize: ConversationAccess,
) {
  const mine = PendingConversationDraftSchema.parse(items).filter(
    (item) => item.threadKey === recipient.id,
  );
  for (const { source } of mine) {
    try {
      await authorize({ source, recipient, operation: "send" });
    } catch {
      throw new Error(CONVERSATION_UNAVAILABLE);
    }
  }
  return mine.map(({ source }) => source);
}

/** Binding to threads.reference / threads.send at the pinned API handoff.
 * Contexts the API cannot resolve must fail, never degrade to a different conversation.
 */
export function conversationReferenceInput(source: ConversationIdentity) {
  const identity = ConversationIdentitySchema.parse(source);
  if (identity.peerBotId || identity.routineId) throw new Error(CONVERSATION_UNAVAILABLE);
  return {
    botId: identity.id!,
    sourceSpaceId: identity.spaceId,
    ...(identity.messageId ? { messageId: identity.messageId } : {}),
  };
}
